import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Button, HeadingSection } from '@innovamat/glimmer-components';
import { useSchoolTeachers } from '../../hooks/use-school-teachers';

import { sortByTwoFields } from '../../utils/sort';
import { sortByOwner } from './teacher-row';

import {
  UpdatePeriodAnnouncementPanel,
  useCheckIsUpdatePeriod,
} from '../../../components/update-period-announcement-panel';

import { useOrganization } from '../../hooks/use-organization';
import { InviteTeachersToOrganizationModal } from './components/invite-teachers-to-organization-modal';
import { SchoolTeachersTable } from './components/school-teachers-table';
import { SchoolTeachersTableSkeleton } from './components/school-teachers-table/school-teachers-table.skeleton';

function SchoolTeachers(): JSX.Element {
  const { organizationId = '' } = useParams();
  const [isAddTeacherToOrgModalOpen, setIsAddTeacherToOrgModalOpen] =
    useState(false);
  const { t } = useTranslation();

  const isUpdatePeriod = useCheckIsUpdatePeriod();

  const { isPlatformEnabled, loading: isLoadingOrg } = useOrganization();

  const {
    data: teachers,
    isLoading,
    refetch,
  } = useSchoolTeachers({
    organizationId,
    isPlatformEnabled,
  });

  const isLoadingTeachers = isLoading || isLoadingOrg;

  const onSuccessAddTeachers = (): void => {
    refetch();
  };

  const sorted = useMemo(() => {
    return (
      teachers
        .sort((a, b) =>
          sortByTwoFields(a.lastName, a.firstName, b.lastName, b.firstName)
        )
        .sort(sortByOwner) || []
    );
  }, [teachers]);

  return (
    <>
      <HeadingSection
        title={t('reports.admin.title.teachers')}
        actions={
          <Button
            rightIcon="AddUpIcon"
            variant="secondary"
            dataTestId="add-teachers-button"
            onClick={() => setIsAddTeacherToOrgModalOpen(true)}
          >
            {t('teachers.buttons.addTeachers')}
          </Button>
        }
      />

      <UpdatePeriodAnnouncementPanel />

      {isLoadingTeachers ? (
        <SchoolTeachersTableSkeleton />
      ) : (
        <SchoolTeachersTable
          isPlatformEnabled={isPlatformEnabled}
          isUpdatePeriod={isUpdatePeriod}
          onAddTeachers={() => setIsAddTeacherToOrgModalOpen(true)}
          organizationId={organizationId}
          teachers={sorted}
        />
      )}

      {isAddTeacherToOrgModalOpen && (
        <InviteTeachersToOrganizationModal
          onClose={() => setIsAddTeacherToOrgModalOpen(false)}
          onSuccess={onSuccessAddTeachers}
        />
      )}
    </>
  );
}

export { SchoolTeachers };
